import { Fragment, useEffect, useId, useMemo, useRef, useState, type FormEvent, type KeyboardEvent } from 'react';
import { BUILDER_WRAPPER, DEFAULT_WRAPPER, PRO_MODES, modeForWrapper, type EngineState, type ProductDef, type WrapperDef } from '../pages/Chat';
import { PickerGlyph, PickerGlyphSvg } from './pickerIcons';
import type { AttachedFile } from './Messages';
import { transcribeBlob, pickAudioMime } from './voice';

/** Files the data profiler can read (mirrors ANALYZABLE_EXTS on the server). */
const ANALYZABLE = /\.(csv|tsv|xlsx|xls)$/i;
const isAnalyzable = (name: string): boolean => ANALYZABLE.test(name.trim());

/**
 * A single, continuously-flowing coffee-brew animation for the send button while
 * a response streams: beans drop into the cup → coffee fills → steam rises →
 * loop. One scene with real motion (all CSS-driven, see `.brew-cup-anim*` in
 * legacy.css) — NOT a cross-fade of separate stills. Inline SVG so it's crisp at
 * any size, transparent, and theme-safe (no baked background). Purely decorative
 * (aria-hidden); the busy/stop state is announced on the button itself.
 */
function BrewBrewingIcon() {
  // Unique per instance so the interior clip never collides if two composers mount.
  const clipId = useId();
  return (
    <svg className="brew-cup-anim" viewBox="0 0 48 48" fill="none" aria-hidden="true">
      <defs>
        <clipPath id={clipId}>
          <path d="M16 21.5 L32 21.5 L30.4 35.2 A2.8 2.8 0 0 1 26.8 38 L21.2 38 A2.8 2.8 0 0 1 17.6 35.2 Z" />
        </clipPath>
      </defs>
      {/* cup body + handle + rim (coffee is drawn on top of the interior) */}
      <path
        className="brew-cup-anim__body"
        d="M14 21 L34 21 L32.2 36 A4 4 0 0 1 28.3 39.5 L19.7 39.5 A4 4 0 0 1 15.8 36 Z"
        fill="#F2E7D3" stroke="#5A3A22" strokeWidth="2" strokeLinejoin="round"
      />
      <path
        d="M34 24.5 C39.6 24.5 40.6 31.6 34.4 33.6"
        fill="none" stroke="#5A3A22" strokeWidth="2.4" strokeLinecap="round"
      />
      <ellipse cx="24" cy="21" rx="10" ry="2.6" fill="#E7D8BE" stroke="#5A3A22" strokeWidth="2" />
      {/* coffee that rises while brewing, then drains before the loop restarts */}
      <g clipPath={`url(#${clipId})`}>
        <g className="brew-cup-anim__fill">
          <rect x="13" y="18" width="22" height="26" fill="#6B4326" />
          <ellipse cx="24" cy="18" rx="9.6" ry="1.5" fill="#8A5A34" />
        </g>
      </g>
      {/* steam once the cup is full */}
      <path
        className="brew-cup-anim__wisp brew-cup-anim__wisp--1"
        d="M20.5 16 q-2.2 -2.4 0 -4.8 q2.2 -2.4 0 -4.8"
        stroke="#EFE6D6" strokeWidth="1.7" strokeLinecap="round" fill="none"
      />
      <path
        className="brew-cup-anim__wisp brew-cup-anim__wisp--2"
        d="M27.5 16 q2.2 -2.4 0 -4.8 q-2.2 -2.4 0 -4.8"
        stroke="#EFE6D6" strokeWidth="1.7" strokeLinecap="round" fill="none"
      />
      {/* two beans dropping into the cup at the start of each loop */}
      <g className="brew-cup-anim__bean brew-cup-anim__bean--1">
        <ellipse cx="0" cy="0" rx="2.7" ry="3.7" fill="#6F4A2E" stroke="#4A3120" strokeWidth="1" />
        <path d="M0 -3 C1.5 -1 1.5 1 0 3" stroke="#C89A6A" strokeWidth="1" fill="none" />
      </g>
      <g className="brew-cup-anim__bean brew-cup-anim__bean--2">
        <ellipse cx="0" cy="0" rx="2.7" ry="3.7" fill="#6F4A2E" stroke="#4A3120" strokeWidth="1" />
        <path d="M0 -3 C1.5 -1 1.5 1 0 3" stroke="#C89A6A" strokeWidth="1" fill="none" />
      </g>
    </svg>
  );
}

interface Props {
  value: string;
  onChange(v: string): void;
  onSubmit(): void;
  onStop(): void;
  streaming: boolean;
  /** Available wrappers (fetched from /api/wrappers) — power the picker + "/" menu. */
  wrappers: WrapperDef[];
  /** The wrapper this session routes to. Sent verbatim as `model`. */
  selectedWrapper: string;
  /** Pin the session to a wrapper (explicit selection). */
  onSelectWrapper(name: string): void;
  /** Linked products — power the "/" command menu (switch surface + use-cases). */
  products?: ProductDef[];
  /** Currently-active product id (null = native Brew) — highlighted in the menu. */
  activeProductId?: string | null;
  /** Switch the active chat surface (null = native Brew). */
  onPickConnector?(id: string | null): void;
  /** Called with the selected File when the user picks an attachment. */
  onAttach?(file: File): void;
  /** Files uploaded but not yet sent — shown as removable chips above the input. */
  pendingAttachments?: AttachedFile[];
  /** Run the one-click data analysis on an attached spreadsheet/CSV. */
  onQuickAnalyze?(file: AttachedFile): void;
  /** Build a downloadable HTML dashboard from an attached spreadsheet/CSV. */
  onBuildDashboard?(file: AttachedFile): void;
  onBuildDeck?(file: AttachedFile): void;
  /** Remove a pending attachment before sending. */
  onRemoveAttachment?(fileId: string): void;
  /** True while an upload is in flight (shows a spinner chip). */
  uploading?: boolean;
  /** Token Saver mode — fewer tool calls / no MCP composer LLM per turn. */
  tokenSaver?: boolean;
  /** Toggle Token Saver on/off. */
  onToggleTokenSaver?(): void;
  /** Speak Replies (Voice AI): when on, each finished assistant reply is spoken. */
  speakReplies?: boolean;
  onToggleSpeakReplies?(): void;
  /** Hands-free live voice conversation: open the immersive VoiceMode overlay. */
  voiceModeOpen?: boolean;
  onOpenVoiceMode?(): void;
  /** Whether to surface the inline Token Saver pill (only once usage ≥ 50%). */
  showTokenSaver?: boolean;
  /** Bridge lifecycle for the Builder's engine, when the Brew Code wrapper is
   *  selected. undefined = nothing has started it yet. Only Brew Code spawns a
   *  process, so it's the only wrapper whose state can be wrong. */
  engineState?: EngineState | undefined;
}

/** One row in the picker: pick a wrapper (route the session), switch the chat
 *  surface, or drop a ready-made use-case prompt. */
type SlashItem =
  | { kind: 'wrapper'; name: string; icon: string; label: string; desc: string }
  | { kind: 'surface'; id: string | null; icon: string; label: string; desc: string }
  | { kind: 'prompt'; icon: string; text: string; product: string };

const SLASH_SECTION: Record<SlashItem['kind'], string> = {
  wrapper: 'Wrappers',
  surface: 'Switch surface',
  prompt: 'Use-cases',
};

/** Display name for a wrapper — the default wrapper reads as "Brew Native" to
 *  match the surface label and the product's framing of the baseline. */
function wrapperLabel(w: WrapperDef): string {
  const label = w.name === DEFAULT_WRAPPER ? 'Brew Native' : w.displayName;
  return label && label.startsWith('Brew') ? label.replace(/^Brew/, 'Breww') : label;
}
// (wrapperIcon / BUILDER_DESC removed — the picker now renders the fixed four
//  PRO_MODES, so per-wrapper icon/description lookups are no longer needed here.)

export function Composer({
  value, onChange, onSubmit, onStop, streaming,
  wrappers, selectedWrapper, onSelectWrapper,
  products = [], activeProductId = null, onPickConnector,
  onAttach, tokenSaver = false, onToggleTokenSaver, showTokenSaver = true,
  speakReplies = false, onToggleSpeakReplies,
  voiceModeOpen = false, onOpenVoiceMode,
  engineState,
  pendingAttachments = [], onRemoveAttachment, onQuickAnalyze, onBuildDashboard, onBuildDeck, uploading = false,
}: Props) {
  const taRef = useRef<HTMLTextAreaElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [pickerOpen, setPickerOpen] = useState(false);
  const [slashIndex, setSlashIndex] = useState(0);

  // ── Voice dictation (🎤) ──────────────────────────────────────────────────
  // Record mic audio, POST it to /api/transcribe (catalog-driven STT), and drop
  // the transcript into the composer FOR REVIEW — never auto-submit. Uses a bare
  // fetch (not the api wrapper, which forces Content-Type: application/json and
  // would corrupt the audio body) with the bearer added manually.
  const [micState, setMicState] = useState<'idle' | 'recording' | 'transcribing'>('idle');
  const mediaRecRef = useRef<MediaRecorder | null>(null);
  const audioChunksRef = useRef<Blob[]>([]);

  const startRecording = async (): Promise<void> => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const mime = pickAudioMime();
      const rec = new MediaRecorder(stream, mime ? { mimeType: mime } : undefined);
      audioChunksRef.current = [];
      rec.ondataavailable = (e) => { if (e.data && e.data.size) audioChunksRef.current.push(e.data); };
      rec.onstop = async () => {
        stream.getTracks().forEach((t) => t.stop());
        const blob = new Blob(audioChunksRef.current, { type: rec.mimeType || 'audio/webm' });
        audioChunksRef.current = [];
        if (!blob.size) { setMicState('idle'); return; }
        setMicState('transcribing');
        try {
          const { text } = await transcribeBlob(blob);
          if (text) onChange(value.trim() ? `${value.trim()} ${text}` : text); // review, never auto-submit
        } catch { /* transient — the user can retry */ }
        setMicState('idle');
        taRef.current?.focus();
      };
      mediaRecRef.current = rec;
      rec.start();
      setMicState('recording');
    } catch {
      setMicState('idle'); // mic permission denied / no device
    }
  };

  const toggleMic = (): void => {
    if (micState === 'recording') { mediaRecRef.current?.stop(); mediaRecRef.current = null; return; }
    if (micState === 'idle') void startRecording();
  };

  const activeWrapper = wrappers.find((w) => w.name === selectedWrapper);
  // The active MODE for the trigger chip. A persona/product wrapper folds under
  // Pro ("✦ Pro · Health") so the surface vocabulary stays the four modes.
  const activeMode = modeForWrapper(selectedWrapper);
  const isModeWrapper = selectedWrapper === DEFAULT_WRAPPER || PRO_MODES.some((m) => m.wrapper === selectedWrapper);
  const modeTriggerLabel = isModeWrapper
    ? activeMode.label
    : `Pro · ${activeWrapper ? wrapperLabel(activeWrapper) : 'Expert'}`;

  // Surfaces shown in BOTH the "/" palette and the dropdown — one row per
  // linked product. The native Brew surface is intentionally omitted: it's
  // already represented by the "Brew Native" wrapper above, so a separate
  // "Brew (native)" row would be redundant. With no linked products this list
  // is empty and the whole SWITCH SURFACE section drops out.
  const surfaceItems = useMemo<SlashItem[]>(() => (
    products.map((p) => ({ kind: 'surface' as const, id: p.id, icon: p.icon, label: p.name, desc: p.blurb }))
  ), [products]);

  // Wrapper rows — the same list for the "/" palette and the dropdown. Brew
  // Native (the default, auto-routing wrapper) is forced to the TOP; the rest
  // keep their server order.
  //
  // Brew Code is HIDDEN from the picker. Its engine is a generic OpenAI client,
  // so it authenticates with a single shared service key and cannot forward the
  // end user (Hermes sets X-Brew-On-Behalf-Of; a plain OpenAI client has no way
  // to add a header). Every user's Builder tokens therefore pooled into one
  // brewcode-service bucket, which blew past the 10M/30d allowance — so users
  // sitting at 12% of their OWN quota got "usage_limit_exceeded" and could not
  // use it at all.
  //
  // Hiding the entry rather than deleting the feature: the Builder itself works,
  // it is only mis-metered, and the file-creating turns it served now route to
  // the Hermes agent (which meters per user correctly). Brew Code remains
  // available as the VS Code extension. Drop this filter once the bridge passes
  // c.userId through — the constructor next to it already does exactly that for
  // Hermes.
  // Breww Pro brief §2: the menu is now the four MODES (Auto / Fast / Pro / Image),
  // not the raw wrapper list. Auto is the default; personas/products fold under Pro
  // and stay reachable via the product switcher, so the choice stays a clean four.
  const wrapperItems = useMemo<SlashItem[]>(
    () => PRO_MODES.map((m) => ({ kind: 'wrapper' as const, name: m.wrapper, icon: m.icon, label: m.label, desc: m.desc })),
    [],
  );

  // Dropdown (caret) shows wrappers + surfaces — mirrors the design's
  // WRAPPERS / SWITCH SURFACE picker.
  const pickerItems = useMemo<SlashItem[]>(
    () => [...wrapperItems, ...surfaceItems],
    [wrapperItems, surfaceItems],
  );

  // "/" command palette — opens whenever the input starts with "/". Text after
  // the slash filters. Same wrappers + surfaces as the dropdown, plus ready-made
  // use-case prompts.
  const slashOpen = value.startsWith('/');
  const slashItems = useMemo<SlashItem[]>(() => {
    if (!slashOpen) return [];
    const q = value.slice(1).toLowerCase().trim();
    const prompts: SlashItem[] = products.flatMap((p) =>
      p.examples.map((text) => ({ kind: 'prompt' as const, icon: p.icon, text, product: p.name })),
    );
    const all = [...wrapperItems, ...surfaceItems, ...prompts];
    if (!q) return all;
    const hit = (s: string) => s.toLowerCase().includes(q);
    return all.filter((it) =>
      it.kind === 'prompt' ? (hit(it.text) || hit(it.product)) : (hit(it.label) || hit(it.desc)),
    );
  }, [slashOpen, value, wrapperItems, surfaceItems, products]);

  // Reset the highlight whenever the input (and thus the filtered list) changes.
  useEffect(() => { setSlashIndex(0); }, [value]);

  // The single picker panel — "/" takes priority over the caret dropdown.
  const inSlash = slashOpen && slashItems.length > 0;
  const menuOpen = inSlash || pickerOpen;
  const menuItems = inSlash ? slashItems : pickerItems;

function pickSlash(it: SlashItem): void {
  if (it.kind === 'wrapper') {
    onSelectWrapper(it.name);
    // Picking Brew Native means "let the engine auto-identify the product" — so
    // also clear any pinned product scope; otherwise it stays pinned to a product
    // and can't auto-route/switch. Other wrappers leave the scope untouched.
    if (it.name === DEFAULT_WRAPPER) onPickConnector?.(null);
    onChange('');
  }
  else if (it.kind === 'surface') {
    onPickConnector?.(it.id);

    // Brew Native surface should also reset wrapper
    if (it.id === null) {
      onSelectWrapper(DEFAULT_WRAPPER);
    }

    onChange('');
  }
  else {
    onChange(it.text);
  }

  setPickerOpen(false);
  taRef.current?.focus();
}

  // Auto-grow the textarea up to a cap.
  useEffect(() => {
    const ta = taRef.current;
    if (!ta) return;
    ta.style.height = 'auto';
    ta.style.height = Math.min(220, ta.scrollHeight) + 'px';
  }, [value]);

  // Close the wrapper picker on outside click. The panel now lives at the form
  // level (so it can span the full composer width like the "/" palette), so the
  // allowlist covers both the trigger (.wrapper-select) and the panel itself.
  useEffect(() => {
    if (!pickerOpen) return;
    const close = (e: MouseEvent) => {
      const t = e.target as HTMLElement;
      if (!t.closest('.wrapper-select, .wrapper-panel')) setPickerOpen(false);
    };
    document.addEventListener('click', close);
    return () => document.removeEventListener('click', close);
  }, [pickerOpen]);

  function handleSubmit(e: FormEvent<HTMLFormElement>): void {
    e.preventDefault();
    if (streaming) onStop(); else onSubmit();
  }
  function handleKey(e: KeyboardEvent<HTMLTextAreaElement>): void {
    if (slashOpen && slashItems.length > 0) {
      if (e.key === 'ArrowDown') { e.preventDefault(); setSlashIndex((i) => (i + 1) % slashItems.length); return; }
      if (e.key === 'ArrowUp')   { e.preventDefault(); setSlashIndex((i) => (i - 1 + slashItems.length) % slashItems.length); return; }
      if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); pickSlash(slashItems[slashIndex] ?? slashItems[0]!); return; }
      if (e.key === 'Escape') { e.preventDefault(); onChange(''); return; }
    }
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      if (!streaming) onSubmit();
    }
  }

  return (
    <form className="composer" id="composer" onSubmit={handleSubmit} style={{ position: 'relative' }}>
      <div className="composer__card">
        {menuOpen && (
          <div className="wrapper-panel" role="listbox" aria-label="Pick a wrapper">
            {inSlash && (
              <div className="wrapper-panel__hint">type to filter · ↑↓ move · ↵ pick · esc close</div>
            )}
            {menuItems.map((it, i) => {
              const header = (i === 0 || menuItems[i - 1]!.kind !== it.kind)
                ? SLASH_SECTION[it.kind]
                : null;
              const selected = it.kind === 'wrapper'
                ? it.name === selectedWrapper
                : it.kind === 'surface' && it.id === activeProductId;
              const highlighted = inSlash && i === slashIndex;
              return (
                <Fragment key={i}>
                  {header && <div className="wrapper-panel__section">{header}</div>}
                  <button
                    type="button" role="option" aria-selected={highlighted}
                    className="picker-row"
                    data-selected={selected || undefined}
                    data-active={highlighted || undefined}
                    onMouseEnter={() => { if (inSlash) setSlashIndex(i); }}
                    onClick={() => pickSlash(it)}
                  >
                    <PickerGlyph icon={it.icon} label={it.kind === 'prompt' ? it.product : it.label} />
                    {it.kind === 'prompt' ? (
                      <span className="picker-row__text">
                        <span className="picker-row__name">{it.text}</span>
                        <span className="picker-row__desc">{it.product}</span>
                      </span>
                    ) : (
                      <span className="picker-row__text">
                        <span className="picker-row__name">{it.label}</span>
                        <span className="picker-row__desc">{it.desc}</span>
                      </span>
                    )}
                    <svg className="picker-row__check" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                      <path d="M20 6 9 17l-5-5" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round" />
                    </svg>
                  </button>
                </Fragment>
              );
            })}
          </div>
        )}
        {(pendingAttachments.length > 0 || uploading) && (
          <div className="composer__attachments" aria-label="Attached files">
            {pendingAttachments.map((a) => (
              <span key={a.fileId} className="attach-chip attach-chip--pending" title={a.name}>
                <span className="attach-chip__icon" aria-hidden="true">{a.kind === 'image' ? '🖼️' : '📄'}</span>
                <span className="attach-chip__name">{a.name}</span>
                {onRemoveAttachment && (
                  <button
                    type="button"
                    className="attach-chip__remove"
                    aria-label={`Remove ${a.name}`}
                    title="Remove"
                    onClick={() => onRemoveAttachment(a.fileId)}
                  >×</button>
                )}
              </span>
            ))}
            {uploading && (
              <span className="attach-chip attach-chip--loading">
                <span className="attach-chip__spinner" aria-hidden="true" />
                <span className="attach-chip__name">Uploading…</span>
              </span>
            )}
            {/* Spreadsheet attached → offer the two one-click deliverables
                rather than making the user think of a prompt for their own
                file: a written analysis, or a dashboard they can download. */}
            {(() => {
              const sheet = pendingAttachments.find((a) => isAnalyzable(a.name));
              if (!sheet) return null;
              return (
                <>
                  {onQuickAnalyze && (
                    <button
                      type="button"
                      className="attach-chip attach-chip--analyze"
                      title={`Analyse ${sheet.name}`}
                      onClick={() => onQuickAnalyze(sheet)}
                    >
                      <span aria-hidden="true">⚡</span>
                      <span className="attach-chip__name">Quick analysis</span>
                    </button>
                  )}
                  {onBuildDashboard && (
                    <button
                      type="button"
                      className="attach-chip attach-chip--analyze"
                      title={`Build a downloadable dashboard from ${sheet.name}`}
                      onClick={() => onBuildDashboard(sheet)}
                    >
                      <span aria-hidden="true">📊</span>
                      <span className="attach-chip__name">Dashboard</span>
                    </button>
                  )}
                  {onBuildDeck && (
                    <button
                      type="button"
                      className="attach-chip attach-chip--analyze"
                      title={`Build a presentable slide deck from ${sheet.name}`}
                      onClick={() => onBuildDeck(sheet)}
                    >
                      <span aria-hidden="true">📑</span>
                      <span className="attach-chip__name">Deck</span>
                    </button>
                  )}
                </>
              );
            })()}
          </div>
        )}
        <textarea
          ref={taRef}
          id="input"
          className="composer__input"
          placeholder="Ask Breww anything…"
          rows={1}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          onKeyDown={handleKey}
          onPaste={(e) => {
            // Paste an image from the clipboard (e.g. right-click "Copy image" on a
            // generated image, or a screenshot) → attach it via the same flow as
            // the file picker. Only cancels the default paste when we actually take
            // an image, so pasting text is unaffected.
            const items = e.clipboardData?.items;
            if (!items || !onAttach) return;
            for (const it of Array.from(items)) {
              if (it.kind === 'file' && it.type.startsWith('image/')) {
                const blob = it.getAsFile();
                if (!blob) continue;
                e.preventDefault();
                const ext = (it.type.split('/')[1] || 'png').split('+')[0];
                onAttach(new File([blob], `pasted-image-${Date.now()}.${ext}`, { type: it.type }));
              }
            }
          }}
          disabled={streaming}
        />
        <div className="composer__bar">
          {/* Everything except Send wraps inside .composer__tools so a narrow
              composer (artifact/dashboard split) never pushes a control past the
              card edge. See the @container rules in legacy.css. */}
          <div className="composer__tools">
          <input
            ref={fileInputRef}
            type="file"
            accept="image/*,.pdf,.doc,.docx,.ppt,.pptx,.xls,.xlsx,.csv,.md,.markdown,.txt,.rtf,.json,.html,.htm"
            style={{ display: 'none' }}
            onChange={(e) => {
              const file = e.target.files?.[0];
              if (file) onAttach?.(file);
              e.target.value = '';
            }}
          />
          <button
            type="button"
            className="composer__icon"
            title="Attach image or file"
            aria-label="Attach"
            onClick={() => fileInputRef.current?.click()}
          >
            <svg viewBox="0 0 24 24" fill="none" aria-hidden="true">
              <path d="M21 12.5l-9.2 9.2a5 5 0 0 1-7.1-7.1l9.2-9.2a3.5 3.5 0 0 1 5 5L9.7 19.7a2 2 0 0 1-2.8-2.8L15 8.6" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
          </button>
          {/* Voice dictation — record mic audio, transcribe (STT), drop the text
              into the input for review. Reuses the recording CSS in legacy.css. */}
          <button
            type="button"
            className={`composer__icon${micState === 'recording' ? ' composer__icon--recording composer__icon--active' : micState === 'transcribing' ? ' composer__icon--transcribing' : ''}`}
            title={micState === 'recording' ? 'Stop recording' : micState === 'transcribing' ? 'Transcribing…' : 'Dictate — record & transcribe'}
            aria-label="Voice input"
            aria-pressed={micState === 'recording'}
            disabled={micState === 'transcribing' || streaming}
            onClick={toggleMic}
          >
            <svg viewBox="0 0 24 24" fill="none" aria-hidden="true">
              <rect x="9" y="3" width="6" height="11" rx="3" stroke="currentColor" strokeWidth="1.7" />
              <path d="M6 11a6 6 0 0 0 12 0M12 17v4M8.5 21h7" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
          </button>
          {/* Live voice — open the immersive hands-free conversation (mic → STT →
              model → TTS → mic, no typing). The animated orb runs in VoiceMode. */}
          {onOpenVoiceMode && (
            <button
              type="button"
              className={`composer__icon composer__icon--voicelive${voiceModeOpen ? ' is-open' : ''}`}
              title="Live voice — hands-free conversation"
              aria-label="Start live voice conversation"
              aria-pressed={voiceModeOpen}
              onClick={onOpenVoiceMode}
            >
              <svg viewBox="0 0 24 24" fill="none" aria-hidden="true">
                <path d="M4 10v4M8 6v12M12 3v18M16 6v12M20 10v4" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" />
              </svg>
            </button>
          )}


          {/* Wrapper picker trigger. Picking a wrapper routes the whole session
              to it (sent verbatim as `model`); the default is Brew Native. The
              same panel is reachable by typing "/". */}
          <div className="mode-select wrapper-select" id="wrapperSelect">
            <button
              type="button"
              className="mode-select__trigger"
              title="Pick the wrapper for this chat"
              aria-haspopup="listbox"
              aria-expanded={pickerOpen}
              onClick={() => setPickerOpen((o) => !o)}
            >
              <PickerGlyphSvg
                icon={activeMode.icon}
                label={modeTriggerLabel}
                size={16}
              />
              <span className="mode-select__label">
                {modeTriggerLabel}
              </span>
              <svg className="mode-select__caret" viewBox="0 0 10 10" fill="none" aria-hidden="true">
                <path d="M2 3.5L5 6.5L8 3.5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
            </button>
          </div>

          {/* Token Saver toggle — when on, the engine uses fewer tool calls and
              skips the MCP composer LLM, so each turn spends fewer tokens. */}
          {onToggleTokenSaver && showTokenSaver && (
            <button
              type="button"
              className={`token-saver-toggle${tokenSaver ? ' is-on' : ''}`}
              aria-pressed={tokenSaver}
              title={tokenSaver
                ? 'Token Saver is on — Brew uses fewer tool calls. Click to turn off.'
                : 'Turn on Token Saver — Brew uses fewer tool calls to spend fewer tokens.'}
              onClick={onToggleTokenSaver}
            >
              <span className="token-saver-toggle__dot" aria-hidden="true" />
              <span className="pill__label">Token Saver{tokenSaver ? ' on' : ''}</span>
            </button>
          )}


          {/* Speak Replies toggle — when on, each finished reply is spoken aloud
              (OpenAI TTS). Off by default; speech is a paid, opt-in action. With the
              mic, this is the "hear it back" half of a live voice conversation. */}
          {onToggleSpeakReplies && (
            <button
              type="button"
              className={`token-saver-toggle speak-replies-toggle${speakReplies ? ' is-on' : ''}`}
              aria-pressed={speakReplies}
              title={speakReplies
                ? 'Speak Replies is on — Brew reads each answer aloud. Click to turn off.'
                : 'Turn on Speak Replies — Brew reads each answer aloud (voice conversation).'}
              onClick={onToggleSpeakReplies}
            >
              <span className="pill__emoji" aria-hidden="true">🔊</span>
              <span className="token-saver-toggle__dot" aria-hidden="true" />
              <span className="pill__label">Speak{speakReplies ? ' on' : ''}</span>
            </button>
          )}

          {/* Builder engine state. Shown ONLY for the Brew Code wrapper: it is
              the one mode that spawns a real process, so it is the one whose
              state can be stale, warming or dead. Every other wrapper is a model
              choice with nothing to be up or down. */}
          {selectedWrapper === BUILDER_WRAPPER && (
            <span className={`agent-picker__state is-${engineState ?? 'idle'}`} title="Brew Code runs an engine in your workspace to write real files.">
              <span className="agent-picker__dot" aria-hidden="true" />
              {engineState === 'ready' ? 'connected'
                : engineState === 'warming' ? 'starting...'
                : engineState === 'stopped' ? 'stopped'
                : 'not started'}
            </span>
          )}

          {/* Agent routing is decided automatically per prompt by the intent
              classifier — file creation / image generation / analyzing an
              uploaded doc route to the agent, everything else is a normal chat
              reply — so the manual "Agent" toggle was removed. */}

          </div>{/* .composer__tools */}

          <button
            type="submit"
            className={`composer__send${streaming ? ' is-streaming' : ''}`}
            aria-label={streaming ? 'Stop generating' : 'Send message'}
            aria-busy={streaming}
            disabled={!streaming && !value.trim()}
          >
            {!streaming ? (
              // Clean upward "send" arrow for the idle state (replaced the coffee-bean
              // glyph). The coffee theme now lives in the streaming animation instead.
              <svg className="composer__send-icon" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                <path d="M12 20V5.5M12 5.5l-6.5 6.5M12 5.5l6.5 6.5" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
            ) : (
              // Streaming: one continuously-flowing coffee brew (beans → fill →
              // steam → loop) plays INSIDE the button while it keeps its stop
              // action — clicking (or submitting) cancels via onStop. The bean-stop
              // glyph is revealed on hover/focus and under prefers-reduced-motion,
              // so "click to stop" and the busy state are never conveyed by the
              // animation alone (aria-busy + aria-label carry it for assistive tech).
              <span className="composer__send-brew" aria-hidden="true">
                <BrewBrewingIcon />
                {/* Coffee-bean "stop" — revealed on hover / reduced-motion. A clean
                    tilted bean (fill-rule evenodd cuts the crease so the button
                    colour shows through); clicking cancels generation. */}
                <svg className="composer__stop-icon" viewBox="0 0 100 120" fill="currentColor" fillRule="evenodd" aria-hidden="true">
                  <g transform="rotate(-20 50 60)">
                    <path d="M50 13 C68 13 82 34 82 60 C82 86 68 107 50 107 C32 107 18 86 18 60 C18 34 32 13 50 13 Z M48 24 C55 44 41 76 48 96 L52 96 C59 76 45 44 52 24 Z" />
                  </g>
                </svg>
              </span>
            )}
          </button>
        </div>
      </div>
      <p className="composer__disclaimer">
        Breww can make mistakes. Please double-check important information.
      </p>
    </form>
  );
}
